<template>
  <v-container>
    <AppLoadingPage v-if="!eventLoaded || !event.id" />

    <template v-else>
      <v-progress-linear
        v-if="$apollo.queries.event.loading"
        height="12"
        indeterminate
        class="my-2"
      />

      <ResultImportPolling
        v-if="showImportProccess"
        :import-status="eventImportResultsStatus"
        class="my-4"
        :show-logs-button="resultsImports.length > 0"
        @scroll-log="scrollToLog"
      />

      <EventResultsTemplate
        :event="event"
        :results-edit-mode="resultsEditMode"
        :competition-class="scrollDetails.competitionClass"
        :can-edit-results="canEditResults"
        @refresh="refetch"
        @refresh-import="$apollo.queries.eventImportResultsStatus.refetch()"
        @editmode="resultsEditMode = $event"
      />

      <p v-if="event.resultsImportedAt">
        {{
          $t(
            "events:EventPage.results.resultsimport.secondarytext",
            "Results imported at",
            "Secondary text for results import in results section on event page"
          )
        }}
        {{ $dayjs.date(event.resultsImportedAt, "fullTime") }}
      </p>

      <v-progress-linear v-if="showImportLogsLoading" indeterminate />

      <ResultImportLogs
        v-if="resultsImports.length > 0"
        ref="eventImportLogs"
        :results-imports="resultsImports"
        :event="event"
      />
    </template>
  </v-container>
</template>
<script>
import { ref, reactive, computed, provide, watch } from "vue";
import gql from "fraql";

import AppLoadingPage from "@elements/AppLoadingPage.vue";
import EventResultsTemplate from "./EventResultsTemplate.vue";
import CompetitionsTable from "../CompetitionsTable.vue";
import EquipeImportMissingComps from "../elements/EquipeImportMissingComps.vue";
import ResultImportPolling from "../import/ResultImportPolling.vue";
import ResultImportLogs from "../import/ResultImportLogs.vue";
import { DisplayTextFragments } from "@composables/display_text";
import { EventResultsProfileFragment } from "../fragments";

export default {
  name: "EventResultsPage",
  components: {
    AppLoadingPage,
    EventResultsTemplate,
    ResultImportPolling,
    ResultImportLogs
  },
  props: {
    eventId: {
      type: String,
      required: true
    }
  },
  setup() {
    const eventImportResultsStatus = ref(null);
    const eventIsInImportingProcess = computed(() =>
      ["QUEUED", "PROCESSING"].includes(eventImportResultsStatus.value)
    );

    const resultsEditModeOn = ref(false);
    const resultEditModeOnBeforeImport = ref(false);

    const resultsEditMode = computed({
      get() {
        return resultsEditModeOn.value;
      },
      set(val) {
        resultsEditModeOn.value = val;
      }
    });

    provide("eventIsInImportingProcess", eventIsInImportingProcess);

    watch(eventIsInImportingProcess, v => {
      if (v === true) {
        resultEditModeOnBeforeImport.value = true;
        resultsEditMode.value = false;
      }
    });

    const scrollDetails = reactive({
      resultsId: null,
      competitionClass: null,
      clearResults() {
        this.resultsId = null;
        this.competitionClass = null;
      }
    });

    provide("scrollToResult", scrollDetails);

    return {
      scrollDetails,

      eventImportResultsStatus,
      eventIsInImportingProcess,
      resultEditModeOnBeforeImport,

      resultsEditModeOn,
      resultsEditMode
    };
  },
  data() {
    return {
      importStatusBecameProcessDuringPolling: false,
      resultsImports: [],
      event: {
        id: null,
        competitions: [],
        allowedMutations: [],
        allowedFields: []
      },
      eventLoaded: false
    };
  },
  computed: {
    canEditResults() {
      return this.$allowedMutation(this.event.allowedMutations, [
        "entryUpdateResult",
        "eventCompetitionBatchUpdateResults"
      ]);
    },
    showImportProccess() {
      if (
        this.eventImportResultsStatus == "PROCESSED" &&
        this.importStatusBecameProcessDuringPolling
      )
        return true;
      return ["QUEUED", "PROCESSING", "FAILED"].includes(
        this.eventImportResultsStatus
      );
    },
    showImportLogsLoading() {
      return (
        this.$allowedMutation(this.event.allowedFields, "resultsImports") &&
        this.$apollo.queries.eventImportResultsStatus.loading
      );
    }
  },
  beforeMount() {
    if (history.state) {
      // Given when coming from person/horse competition result trough event link
      this.scrollDetails.resultsId = history.state.resultsId ?? null;
      this.scrollDetails.competitionClass =
        history.state.competitionClass ?? null;
    }
  },
  apollo: {
    event: {
      query: function () {
        return gql`
          query eventResultsProfile ($id: ID) {
            event (id: $id) {
              id
              startDate
              endDate
              resultsImportedAt
              allowedMutations
              disciplineId
              discipline @client {
                id
                name
              }
              countryId
              country @client {
                id
                name
                alpha2
                ioc
              }
              ${EventResultsProfileFragment}
              ${ResultImportLogs.fragments.eventFields}
              useEquipe
              ${EquipeImportMissingComps.fragments.event}
              ${CompetitionsTable.fragments.competitions}
              competitions {
                id
                forRegistrationOnly
                resultsUnlisted
              }
              allowedFields
            }
          }
        `;
      },
      variables() {
        return {
          id: this.eventId
        };
      },
      result({ loading }) {
        if (loading) return;
        if (!this.eventLoaded) {
          this.resultsEditModeOn = this.canEditResults;
        }

        this.eventLoaded = true;
      },
      fetchPolicy: "cache-and-network"
    },
    eventImportResultsStatus: {
      query() {
        const resultImports = gql`
          fragment _ on Event {
            resultsImports {
              id
              payloadCompetitionCodes
              createdAt
              processingEndedAt
              payloadResultsCount
              ${ResultImportLogs.fragments.resultImportFields}
              feedbackMessagesCount
              feedbackMessages {
                ${DisplayTextFragments.translation}
              }
              status
            }
          }
        `;

        return gql`
          query eventImportResultsStatus($id: ID) {
            event(id: $id) {
              id
              ${
                this.$allowedMutation(
                  this.event.allowedFields,
                  "resultsImportStatus"
                )
                  ? "resultsImportStatus"
                  : ""
              }
              ${
                this.$allowedMutation(
                  this.event.allowedFields,
                  "resultsImports"
                )
                  ? resultImports
                  : ""
              }
              resultsImportedAt
            }
          }
      `;
      },
      manual: true,
      variables() {
        return {
          id: this.eventId
        };
      },
      result({ loading, data }) {
        if (loading) return;
        const { resultsImportStatus, resultsImports } = data.event;
        // if previous data was processing and now returns processed, then show
        // processed text for current session
        if (
          ["PROCESSED", "FAILED"].includes(resultsImportStatus) &&
          this.eventImportResultsStatus === "PROCESSING"
        ) {
          if (this.resultEditModeOnBeforeImport) {
            this.resultsEditMode = true;
            this.resultEditModeOnBeforeImport = false;
          }
          this.importStatusBecameProcessDuringPolling = true;
          this.$apollo.queries.event.refetch();
        }

        this.eventImportResultsStatus = resultsImportStatus ?? null;
        this.resultsImports = resultsImports ?? [];

        switch (resultsImportStatus) {
          case "QUEUED":
          case "PROCESSING":
            this.$apollo.queries.eventImportResultsStatus.startPolling(10000);
            break;
          default:
            this.$apollo.queries.eventImportResultsStatus.stopPolling();
        }
      }
    }
  },
  methods: {
    refetch() {
      this.$apollo.queries.event.refetch();
    },
    scrollToLog() {
      if (this.$refs.eventImportLogs?.$el) {
        this.$refs.eventImportLogs.$el.scrollIntoView();
      }
    }
  }
};
</script>
